import { Dialog, DialogActions, DialogContent } from '@mui/material';
import { CustomDialogTitle } from '../../../Common/Custom/CustomDialogTitle';
import { MDTypography } from '../../../../Component/Common/Components/MDTypography';
import { MDButton } from '../../../../Component/Common/Components/MDButton';

interface PriorityRankModalProps {
    open: boolean;
    onClose: () => void;
}

export function PriorityRankModal({ open, onClose }: PriorityRankModalProps) {
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <CustomDialogTitle onClose={onClose}>Priority Ranking</CustomDialogTitle>
            <DialogContent dividers>
                <MDTypography variant="body2" paragraph>
                    Priority ranks are assigned per occupational level to show which demographic groups most urgently require attention. P1 is the highest priority, the group that has achieved the smallest proportion of its sectoral target.
                </MDTypography>

                <MDTypography variant="body2" sx={{ fontWeight: 600 }} gutterBottom>How priorities are calculated</MDTypography>
                <MDTypography variant="body2" paragraph>
                    Rankings are determined by the following, applied in order:
                </MDTypography>
                <ol>
                    <li style={{ marginBottom: 8 }}>
                        <MDTypography variant="body2" component="span">
                            <strong>Relative % gap</strong> — calculated as (Gap headcount / Target headcount) × 100. The group furthest behind relative to its own sectoral target is ranked P1. This ensures every designated group is measured against its own benchmark, regardless of target size.
                        </MDTypography>
                    </li>
                    <li style={{ marginBottom: 8 }}>
                        <MDTypography variant="body2" component="span">
                            <strong>Headcount gap</strong> — if two groups have the same relative % gap, the group with the larger absolute headcount gap is ranked higher.
                        </MDTypography>
                    </li>
                    <li>
                        <MDTypography variant="body2" component="span">
                            <strong>Sectoral target %</strong> — if two groups still have equal relative and absolute gaps, the group with the higher sectoral determination target is ranked higher. This reflects the relative demographic weight prescribed by the Department of Employment and Labour for your sector.
                        </MDTypography>
                    </li>
                </ol>

                <MDTypography variant="body2" sx={{ fontWeight: 600, mt: 2 }} gutterBottom>Why we use relative % gap instead of absolute gap</MDTypography>
                <MDTypography variant="body2" paragraph>
                    Relative % gap ensures every designated group makes proportional progress toward its sectoral target, which reflects the intent of Section 15A of the Employment Equity Amendment Act: equitable representation of suitably qualified people from all designated groups at every occupational level.
                </MDTypography>

                <MDTypography variant="body2" sx={{ fontWeight: 600 }} gutterBottom>Priority at date of movement</MDTypography>
                <MDTypography variant="body2">
                    For individual recruitment and promotion movements, priority is calculated using the workforce snapshot as at the date of that movement, not the current snapshot. This reflects the actual circumstances at the time the decision was made.
                </MDTypography>
            </DialogContent>
            <DialogActions>
                <MDButton onClick={onClose} variant="contained" color="primary" size="small">Got it</MDButton>
            </DialogActions>
        </Dialog>
    );
}
